/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { max, range, sortBy } from 'lodash';

import { ApiSurveyData } from '@/pages/survey_builder/types';

import * as helpers from './helpers';
import * as types from './mutationTypes';
import { State, Question, QuestionType, QuestionOption, QuestionCondition } from './state';

const addPage = (state: State) => {
  state.totalPages += 1;
  state.pages.push({
    page: state.totalPages,
    isValid: false,
  });
};

const addQuestion = (state: State, idx: number, overrides: Partial<Question>) => {
  const newQuestion: Question = {
    id: getNextQuestionId(state),
    title: '',
    description: '',
    pageNumber: state.currentPage,
    type: undefined,
    required: false,
    rank: 0,
    weight: 1,
    options: [],
    conditions: [],
    mappings: [],
    number: 0,
    isValid: false,
    ...overrides,
  };

  state.questions.splice(idx, 0, newQuestion);

  fixQuestionNumbers(state.questions);

  state.activeQuestionId = newQuestion.id;

  state.isDirty = true;
  fixValidation(state);
};

const addQuestionOption = (state: State, question: Question) => {
  const newOption: QuestionOption = {
    id: getNextOptionId(state),
    title: '',
    multiplier: 1,
  };

  question.options.push(newOption);
};

const removeQuestionOption = (state: State, question: Question, option: QuestionOption) => {
  const idx = question.options.indexOf(option);

  if (idx !== -1) {
    question.options.splice(idx, 1);

    handleOptionRemove(state, option);
  }
};

const removeQuestionOptions = (state: State, question: Question) => {
  const oldOptions = question.options.slice();

  question.options = [];

  oldOptions.forEach(option => {
    handleOptionRemove(state, option);
  });
};

const fixQuestionNumbers = (questions: Question[]) => {
  let nextNumber = 1;

  questions.forEach(question => {
    if (helpers.questionHasNumber(question)) {
      question.number = nextNumber;
      nextNumber += 1;
    } else {
      question.number = -1;
    }
  });
};

const fixValidation = (state: State) => {
  const questionsMap = helpers.getQuestionsMap(state.questions);

  state.questions.forEach(question => {
    question.isValid = helpers.isQuestionValid(questionsMap, question, state.isWeighted);
  });

  state.pages.forEach(page => {
    const hasInvalidQuestions = state.questions.find(question => question.pageNumber == page.page && !question.isValid);
    page.isValid = !hasInvalidQuestions;
  });

  state.isValid = helpers.isSurveyValid(state);
};

const pageNextIdx = (questions: Question[], page: number) => {
  let idx = 0;

  questions.forEach((question, questionIdx) => {
    if (question.pageNumber <= page) {
      idx = questionIdx + 1;
    }
  });

  return idx;
};

const getNextQuestionId = (state: State) => {
  const id = state.nextQuestionId;
  state.nextQuestionId -= 1;
  return id;
};

const getNextOptionId = (state: State) => {
  const id = state.nextOptionId;
  state.nextOptionId -= 1;
  return id;
};

const getNextConditionId = (state: State) => {
  const id = state.nextConditionId;
  state.nextConditionId -= 1;
  return id;
};

const generatePages = (totalPages: number) =>
  range(1, totalPages + 1).map(page => ({
    page,
    isValid: false,
  }));

const handleQuestionRemove = (state: State, question: Question) => {
  const removeQuestionId = question.id;

  if (helpers.questionHasOptions(question)) {
    state.questions.filter(helpers.questionHasConditions).forEach(question => {
      const newConditions = question.conditions.filter(condition => condition.shownByQuestionId !== removeQuestionId);

      if (newConditions.length !== question.conditions.length) {
        question.conditions = newConditions;
      }
    });
  }
};

const handleOptionRemove = (state: State, option: QuestionOption) => {
  const removeOptionId = option.id;

  state.questions.filter(helpers.questionHasConditions).forEach(question => {
    const newConditions = question.conditions.filter(condition => condition.shownByOptionId !== removeOptionId);

    if (newConditions.length !== question.conditions.length) {
      question.conditions = newConditions;
    }
  });
};

const mutations = {
  [types.LOADING](state: State) {
    state.isLoaded = false;
  },
  [types.LOADED](state: State, surveyData: ApiSurveyData) {
    let questions: Question[] = surveyData.questions.map(question => {
      const mappedQuestion = {
        id: question.id,
        title: question.title,
        description: question.description,
        pageNumber: question.page_number,
        type: question.type,
        required: question.required,
        rank: question.rank,
        weight: question.weight != null ? question.weight : 1,
        options: question.options.map(option => ({
          id: option.id,
          title: option.title,
          multiplier: option.multiplier != null ? option.multiplier : 1,
        })),
        conditions: question.conditions.map(condition => ({
          id: getNextConditionId(state),
          shownByQuestionId: condition.shown_by_question_id,
          shownByOptionId: condition.shown_by_option_id,
        })),
        number: 0,
        isValid: false,
      };

      if (surveyData.addMappings) {
        mappedQuestion.mappings = question.mappings
          ? question.mappings.map(mapping => {
              return {
                id: mapping.object_id,
                title: mapping.title,
                type: mapping.object_type,
                mapped: true,
              };
            })
          : [];
      }

      return mappedQuestion;
    });

    questions = sortBy(questions, ['pageNumber', 'rank']);

    fixQuestionNumbers(questions);

    state.surveyId = surveyData.id;
    state.title = surveyData.title;
    state.isWeighted = surveyData.is_weighted;
    state.midRiskThreshold = `${surveyData.mid_risk_threshold}`;
    state.highRiskThreshold = `${surveyData.high_risk_threshold}`;
    state.questions = questions;

    state.totalPages = 1;

    if (state.questions.length) {
      state.totalPages = max(state.questions.map(question => question.pageNumber))!;
    }

    if (state.currentPage > state.totalPages) {
      state.currentPage = state.totalPages;
    }

    state.pages = generatePages(state.totalPages);

    state.activeQuestionId = undefined;

    state.isLoaded = true;
    state.isDirty = false;
    fixValidation(state);
  },
  [types.EMPTY](state: State) {
    state.surveyId = undefined;
    state.title = 'New questionnaire';
    state.isWeighted = false;
    state.midRiskThreshold = '50';
    state.highRiskThreshold = '100';
    state.questions = [];
    state.currentPage = 1;
    state.totalPages = 1;
    state.pages = generatePages(state.totalPages);
    state.activeQuestionId = undefined;
    state.isLoaded = true;
    state.isDirty = false;
    state.isValid = false;

    fixValidation(state);
  },
  [types.SAVING](state: State) {
    state.isSaving = true;
  },
  [types.SAVED](state: State) {
    state.isSaving = false;
    state.isDirty = false;
  },
  [types.SAVING_ERROR](state: State) {
    state.isSaving = false;
  },
  [types.SHOW_PREVIEW](state: State) {
    state.isPreviewVisible = true;
  },
  [types.HIDE_PREVIEW](state: State) {
    state.isPreviewVisible = false;
  },

  [types.SET_TITLE](state: State, title: string) {
    state.title = title;
    state.isDirty = true;
    fixValidation(state);
  },
  [types.SET_WEIGHTED](state: State, isWeighted: boolean) {
    state.isWeighted = isWeighted;
    state.isDirty = true;
    fixValidation(state);
  },
  [types.SET_MID_RISK_THRESHOLD](state: State, midRiskThreshold: string) {
    state.midRiskThreshold = `${midRiskThreshold}`; // force string
    state.isDirty = true;
    fixValidation(state);
  },
  [types.SET_HIGH_RISK_THRESHOLD](state: State, highRiskThreshold: string) {
    state.highRiskThreshold = `${highRiskThreshold}`; // force string
    state.isDirty = true;
    fixValidation(state);
  },
  [types.ADD_QUESTION](state: State) {
    const newQuestionIdx = pageNextIdx(state.questions, state.currentPage);

    addQuestion(state, newQuestionIdx, {});
  },
  [types.ADD_ANOTHER_QUESTION](state: State) {
    const question = helpers.findEditQuestion(state);

    if (question != null) {
      const questionIdx = state.questions.indexOf(question);

      addQuestion(state, questionIdx + 1, {});
    }
  },
  [types.EDIT_QUESTION](state: State, questionId: number) {
    if (state.activeQuestionId === questionId) {
      state.activeQuestionId = undefined;
    } else {
      state.activeQuestionId = questionId;
    }
  },
  [types.SET_QUESTIONS_ORDER](state: State, currentQuestions: Question[]) {
    state.questions = [
      ...state.questions.filter(question => question.pageNumber < state.currentPage),
      ...currentQuestions,
      ...state.questions.filter(question => question.pageNumber > state.currentPage),
    ];

    fixQuestionNumbers(state.questions);

    state.isDirty = true;
  },
  [types.SET_CURRENT_PAGE](state: State, page: number) {
    state.currentPage = page;

    const question = helpers.findEditQuestion(state);

    if (question != null && question.pageNumber != state.currentPage) {
      state.activeQuestionId = undefined;
    }
  },
  [types.ADD_PAGE](state: State) {
    addPage(state);

    state.currentPage = state.totalPages;
    state.activeQuestionId = undefined;
    state.isDirty = true;
    fixValidation(state);
  },
  [types.REMOVE_PAGE](state: State) {
    state.questions
      .filter(question => question.pageNumber === state.currentPage)
      .forEach(question => handleQuestionRemove(state, question));

    state.questions = state.questions.filter(question => question.pageNumber !== state.currentPage);

    state.questions.forEach(question => {
      if (question.pageNumber > state.currentPage) {
        question.pageNumber -= 1;
      }
    });

    fixQuestionNumbers(state.questions);

    if (state.totalPages > 1) {
      state.totalPages -= 1;

      if (state.currentPage > state.totalPages) {
        state.currentPage -= 1;
      }
    }

    state.pages = generatePages(state.totalPages);

    state.activeQuestionId = undefined;

    state.isDirty = true;
    fixValidation(state);
  },

  [types.SET_QUESTION_TYPE](state: State, { questionId, type }: { questionId: number; type: QuestionType }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      if (helpers.additionalTypeQuestionHasText(question)) {
        question.title = helpers.removeHtmlTags(question.title);
      }

      question.type = type;

      if (helpers.questionHasOptions(question)) {
        if (!question.options.length) {
          addQuestionOption(state, question);
        }
      } else {
        // we need to remove options otherwise quesion will be invalid
        removeQuestionOptions(state, question);

        if (question.type === 'additional_text') {
          question.required = false;
          question.weight = 1;
        }
      }

      fixQuestionNumbers(state.questions);
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_TITLE](state: State, { questionId, title }: { questionId: number; title: string }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      question.title = title;
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_DESCRIPTION](
    state: State,
    { questionId, description }: { questionId: number; description: string }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      question.description = description;
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_REQUIRED](state: State, { questionId, required }: { questionId: number; required: boolean }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      question.required = required;
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_WEIGHT](state: State, { questionId, weight }: { questionId: number; weight: number }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      question.weight = weight;
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_PAGE](state: State, { questionId, page }: { questionId: number; page: number | string }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      if (page === 'new') {
        addPage(state);
        page = state.totalPages;
      } else {
        page = parseInt(page as string, 10);
      }

      if (question.pageNumber !== page) {
        const oldIdx = state.questions.indexOf(question);

        if (oldIdx !== -1) {
          state.questions.splice(oldIdx, 1);

          question.pageNumber = page;

          const idx = pageNextIdx(state.questions, page);

          state.questions.splice(idx, 0, question);

          fixQuestionNumbers(state.questions);

          state.currentPage = page;

          state.isDirty = true;
          fixValidation(state);
        }
      }
    }
  },
  [types.SET_QUESTION_OPTION_TITLE](
    state: State,
    { questionId, optionId, title }: { questionId: number; optionId: number; title: string }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const option = helpers.findOption(question, optionId);

      if (option != null) {
        option.title = title;
        state.isDirty = true;
        fixValidation(state);
      }
    }
  },
  [types.SET_QUESTION_OPTION_MULTIPLIER](
    state: State,
    { questionId, optionId, multiplier }: { questionId: number; optionId: number; multiplier: number }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const option = helpers.findOption(question, optionId);

      if (option != null) {
        option.multiplier = multiplier;
        state.isDirty = true;
        fixValidation(state);
      }
    }
  },
  [types.SET_QUESTION_MAPPINGS](state: State, { questionId, mappings }: { questionId: number; mappings: Array<any> }) {
    const question = helpers.findQuestion(state, questionId);
    if (question != null) {
      question.mappings
        ? question.mappings.splice(0, question.mappings.length, ...mappings)
        : (question.mappings = mappings);
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.ADD_QUESTION_OPTION](state: State, questionId: number) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      addQuestionOption(state, question);

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.REMOVE_QUESTION_OPTION](state: State, { questionId, optionId }: { questionId: number; optionId: number }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const option = helpers.findOption(question, optionId);

      if (option == null) {
        return;
      }

      removeQuestionOption(state, question, option);

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.ADD_QUESTION_OPTION_SUBQUESTION](
    state: State,
    { questionId, optionId }: { questionId: number; optionId: number }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const questionIdx = state.questions.indexOf(question);

      const option = helpers.findOption(question, optionId);

      if (option == null) {
        return;
      }

      addQuestion(state, questionIdx + 1, {
        title: 'Please specify:',
        pageNumber: question.pageNumber,
        conditions: [
          {
            id: getNextConditionId(state),
            shownByQuestionId: question.id,
            shownByOptionId: option.id,
          },
        ],
      });
    }
  },
  [types.SET_QUESTION_OPTIONS_ORDER](
    state: State,
    { questionId, options }: { questionId: number; options: QuestionOption[] }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      question.options = options;

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.ADD_QUESTION_CONDITION](state: State, questionId: number) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const newCondition: QuestionCondition = {
        id: getNextConditionId(state),
        shownByQuestionId: undefined,
        shownByOptionId: undefined,
      };

      question.conditions.push(newCondition);

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.REMOVE_QUESTION_CONDITION](
    state: State,
    { questionId, conditionId }: { questionId: number; conditionId: number }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const condition = helpers.findCondition(question, conditionId);

      const idx = question.conditions.indexOf(condition!);

      if (idx !== -1) {
        question.conditions.splice(idx, 1);

        state.isDirty = true;
        fixValidation(state);
      }
    }
  },
  [types.SET_QUESTION_CONDITION_QUESTION](
    state: State,
    {
      questionId,
      conditionId,
      conditionQuestionId,
    }: { questionId: number; conditionId: number; conditionQuestionId: number }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const condition = helpers.findCondition(question, conditionId);

      if (condition != null) {
        if (condition.shownByQuestionId !== conditionQuestionId) {
          condition.shownByOptionId = undefined;
        }
        condition.shownByQuestionId = conditionQuestionId;

        state.isDirty = true;
        fixValidation(state);
      }
    }
  },
  [types.SET_QUESTION_CONDITION_OPTION](
    state: State,
    {
      questionId,
      conditionId,
      conditionOptionId,
    }: { questionId: number; conditionId: number; conditionOptionId: number }
  ) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const condition = helpers.findCondition(question, conditionId);

      if (condition != null) {
        condition.shownByOptionId = conditionOptionId;

        state.isDirty = true;
        fixValidation(state);
      }
    }
  },
  [types.SET_QUESTION_VISIBLE_ALWAYS](state: State, questionId: number) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null && question.conditions.length) {
      question.conditions = [];

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.SET_QUESTION_VISIBLE_CONDITIONAL](state: State, questionId: number) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null && !question.conditions.length) {
      mutations[types.ADD_QUESTION_CONDITION](state, questionId);
      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.REMOVE_QUESTION](state: State, questionId: number) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const idx = state.questions.indexOf(question);

      state.questions.splice(idx, 1);

      fixQuestionNumbers(state.questions);

      handleQuestionRemove(state, question);

      if (questionId === state.activeQuestionId) {
        state.activeQuestionId = undefined;
      }

      state.isDirty = true;
      fixValidation(state);
    }
  },
  [types.DUPLICATE_QUESTION](state: State, { questionId, addMappings = false }) {
    const question = helpers.findQuestion(state, questionId);

    if (question != null) {
      const questionIdx = state.questions.indexOf(question);

      const duplicatedQuestion = {
        title: `${question.title} (copy)`,
        description: question.description,
        pageNumber: question.pageNumber,
        type: question.type,
        required: question.required,
        weight: question.weight,
        options: question.options.map(option => ({
          ...option,
          id: getNextOptionId(state),
        })),
        conditions: question.conditions.map(condition => ({
          ...condition,
          id: getNextConditionId(state),
        })),
      };

      if (addMappings) {
        duplicatedQuestion.mappings = question.mappings.map(mapped => {
          return { ...mapped };
        });
      }

      addQuestion(state, questionIdx + 1, duplicatedQuestion);
    }
  },
};

export default mutations;
